import { FC, useEffect, useRef, useState } from "react";
import { Toggle } from "@app/shared/Toggle";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ClientListItem } from "@app/client/client";
import { AiResponseReviewService } from "@app/operations/ai-response-review/aiResponseReviewService";
import { ClientWeeklyUsage, UtilizationTrendPoint } from "@app/operations/ai-response-review/interfaces";
import { UtilizationTrendChart } from "@app/operations/ai-response-review/UtilizationTrendChart";

const EXTERNAL_ONLY_DISABLED_TOOLTIP =
  "Not applicable when a specific client is selected — a client's utilization is entirely internal or entirely external.";

const UtilizationTrendTab: FC = () => {
  const service = useRef(new AiResponseReviewService()).current;

  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [externalOnly, setExternalOnly] = useState<boolean>(false);
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>();
  const [appliedClientId, setAppliedClientId] = useState<number | undefined>();

  const [trendData, setTrendData] = useState<UtilizationTrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState<boolean>(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const [clientUsage, setClientUsage] = useState<ClientWeeklyUsage | undefined>();
  const [clientUsageLoading, setClientUsageLoading] = useState<boolean>(false);
  const [clientUsageError, setClientUsageError] = useState<string | null>(null);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const result = await service.getUtilizationTrendClients();
        setClients(result);
      } catch (err) {
        console.error("Error loading clients for utilization trend:", err);
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    let ignore = false;
    const loadTrend = async () => {
      setTrendLoading(true);
      setTrendError(null);
      try {
        const result = await service.getUtilizationTrend(appliedClientId);
        if (!ignore) {
          setTrendData(result);
        }
      } catch (err) {
        if (!ignore) {
          setTrendError("Failed to load utilization trend");
        }
        console.error("Error loading utilization trend:", err);
      } finally {
        if (!ignore) {
          setTrendLoading(false);
        }
      }
    };
    loadTrend();
    return () => {
      ignore = true;
    };
  }, [appliedClientId]);

  useEffect(() => {
    if (appliedClientId === undefined) {
      setClientUsage(undefined);
      setClientUsageError(null);
      setClientUsageLoading(false);
      return;
    }

    let ignore = false;
    const loadClientUsage = async () => {
      setClientUsageLoading(true);
      setClientUsageError(null);
      try {
        const result = await service.getClientWeeklyUsage(appliedClientId);
        if (!ignore) {
          setClientUsage(result);
        }
      } catch (err) {
        if (!ignore) {
          setClientUsage(undefined);
          setClientUsageError("Failed to load client weekly usage");
        }
        console.error("Error loading client weekly usage:", err);
      } finally {
        if (!ignore) {
          setClientUsageLoading(false);
        }
      }
    };
    loadClientUsage();
    return () => {
      ignore = true;
    };
  }, [appliedClientId]);

  const handleApply = () => {
    setAppliedClientId(selectedClientId);
    if (selectedClientId !== undefined) {
      setExternalOnly(false);
    }
  };

  const chartData = trendData.map(point => ({
    count: externalOnly ? point.externalCount : point.totalCount,
    isCurrentPeriod: point.isCurrentPeriod,
    weekEndUtc: point.weekEndUtc,
    weekStartUtc: point.weekStartUtc,
  }));

  const chartLabel = externalOnly ? "External Utilization" : "Total Utilization";
  const clientSelectPlaceholder = externalOnly ? "All External Clients" : "All Clients";

  return (
    <div className="utilization-trend-tab">
      <div className="mb-3">
        <TooltipWrapper text={appliedClientId !== undefined ? EXTERNAL_ONLY_DISABLED_TOOLTIP : ""}>
          <Toggle
            testId="utilization-trend-tab__external-only-toggle"
            label="External Utilization Only"
            active={externalOnly}
            disabled={appliedClientId !== undefined}
            onChange={() => setExternalOnly(!externalOnly)}
          />
        </TooltipWrapper>
      </div>

      <div className="d-flex flex-wrap align-items-end gap-3 mb-4 utilization-trend-tab__filters">
        <QESSelect
          label="Client Name"
          testId="utilization-trend-tab__client-select"
          value={selectedClientId?.toString() ?? ""}
          onChange={(e) => setSelectedClientId(e.target.value ? Number(e.target.value) : undefined)}
          allowEmpty={true}
          placeholder={clientSelectPlaceholder}
        >
          {clients.map(client => (
            <option key={client.clientId} value={client.clientId}>{client.name}</option>
          ))}
        </QESSelect>

        <QESButton
          qesType="primary"
          qesTestId="utilization-trend-tab__apply"
          onClick={handleApply}
        >
          Apply
        </QESButton>
      </div>

      {trendError && (
        <div className="alert alert-danger" role="alert">
          {trendError}
        </div>
      )}

      <UtilizationTrendChart data={chartData} label={chartLabel} />

      {trendLoading && (
        <span className="text-muted" data-testid="utilization-trend-tab__trend-loading">Loading trend...</span>
      )}

      {clientUsageError && (
        <div className="alert alert-danger" role="alert">
          {clientUsageError}
        </div>
      )}

      {clientUsageLoading && (
        <span className="text-muted" data-testid="utilization-trend-tab__usage-loading">Loading client usage...</span>
      )}

      {clientUsage && (
        <div className="utilization-trend-tab__client-usage" data-testid="utilization-trend-tab__client-usage">
          <div className="utilization-trend-tab__client-usage-name">
            {clientUsage.clientName}
            {!clientUsage.isUnlimitedPlan && ` (${clientUsage.responsesUsed ?? 0} / ${clientUsage.weeklyLimit})`}
          </div>
          <div>{clientUsage.percentLeftDescription}</div>
        </div>
      )}
    </div>
  );
};

export default UtilizationTrendTab;
